import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Phone, MapPin, Linkedin, MessageCircle, Instagram } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [inquirerType, setInquirerType] = useState("Employer");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const fd = new FormData(form);
    try {
      await api.post("/contact", {
        name: fd.get("name"),
        email: fd.get("email"),
        phone: fd.get("phone"),
        inquirer_type: inquirerType,
        message: fd.get("message"),
      });
      toast.success("Message sent! Our team will reach out shortly.");
      form.reset();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 relative overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 bee-grid-bg opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-bee-orange font-semibold mb-3">Get In Touch</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
              Let&apos;s start the{" "}
              <span className="text-bee-teal">conversation</span>.
            </h2>
            <p className="mt-5 text-base lg:text-lg text-muted-foreground max-w-lg">
              Whether you&apos;re hiring or looking for your next role — drop us a note and our team will respond
              within one business day.
            </p>

            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border" data-testid="contact-detail-email">
                <span className="h-11 w-11 rounded-lg bg-bee-teal text-white inline-flex items-center justify-center">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Email</div>
                  <a href="mailto:info@workingbees.net" className="font-semibold hover:text-bee-teal">info@workingbees.net</a>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border" data-testid="contact-detail-phone">
                <span className="h-11 w-11 rounded-lg bg-bee-orange text-white inline-flex items-center justify-center">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Phone</div>
                  <a href="tel:+919011579799" className="font-semibold hover:text-bee-teal">+91 90115 79799</a>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border" data-testid="contact-detail-address">
                <span className="h-11 w-11 rounded-lg bg-bee-honey text-bee-teal-dark inline-flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Office</div>
                  <div className="font-semibold">Pan-India</div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <a href="#" data-testid="contact-social-linkedin" className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-border hover:bg-bee-teal hover:text-white hover:border-bee-teal transition"><Linkedin className="h-4 w-4" /></a>
              <a href="#" data-testid="contact-social-instagram" className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-border hover:bg-bee-teal hover:text-white hover:border-bee-teal transition"><Instagram className="h-4 w-4" /></a>
              <a href="#" data-testid="contact-social-whatsapp" className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-border hover:bg-bee-teal hover:text-white hover:border-bee-teal transition"><MessageCircle className="h-4 w-4" /></a>
            </div>
          </div>

          <form onSubmit={onSubmit} className="rounded-3xl bg-card border border-border p-6 sm:p-8 shadow-xl" data-testid="contact-form">
            <h3 className="font-display text-2xl font-bold tracking-tight">Send us a message</h3>
            <div className="mt-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" required data-testid="contact-input-name" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" name="email" required data-testid="contact-input-email" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" name="phone" data-testid="contact-input-phone" />
                </div>
                <div>
                  <Label>I am a</Label>
                  <Select value={inquirerType} onValueChange={setInquirerType}>
                    <SelectTrigger data-testid="contact-select-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Employer">Employer</SelectItem>
                      <SelectItem value="Candidate">Candidate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="message">Message / Requirement</Label>
                <Textarea id="message" name="message" rows={5} required data-testid="contact-input-message" />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="w-full h-12 bg-bee-teal text-white hover:bg-[hsl(var(--bee-teal-dark))] rounded-full"
              >
                {submitting ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
